// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  getAssetPath
} from "@stencil/core";
import { MercuryBundles, getIconPath } from "@genexus/mercury";

import { Locale } from "../../common/locale";
import { ANIMATION_DOTS } from "../../common/common";

const COPYRIGHTS_ILLUSTRATION = getAssetPath(
  `./gx-ide-assets/splash/images/copyrights-illustration.svg`
);
const LOGO_NEXT = getAssetPath(
  `./gx-ide-assets/welcome-page/images/icon-next.svg`
);
const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/typography",
  "components/button",
  "components/icon",
  "chameleon/scrollbar"
];

const OPEN_LOG_ICON = getIconPath({
  category: "system",
  name: "launch",
  colorType: "primary"
});

@Component({
  tag: "gx-ide-splash",
  styleUrl: "splash.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/splash"]
})
export class GxIdeUiSplash {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  @Element() el: HTMLGxIdeSplashElement;

  /**
   * Set this to true if installation is in process
   */
  @State() ariaBusy: boolean = true;

  /**
   * The application version to display in the dialog.
   */
  @Prop() readonly appVersion: string;

  /**
   * Callback invoked when the user clicks the 'Download Docker' button.
   */
  @Prop() readonly downloadDockerCallback: () => void;

  /**
   * Callback invoked when the user clicks the quit button.
   */
  @Prop() readonly quitCallback: () => void;

  /**
   * Array of messages to display in the text box.
   * This property updates whenever a message is added,
   * and the scroll should position itself at the bottom to keep the last message visible.
   */
  @Prop() readonly messages: MessageType[] = [];

  /**
   * Callback invoked when the user clicks the 'Open log' link.
   */
  @Prop() readonly openLogCallback: () => void;

  /**
   * Set to `true` to display the quit button.
   */
  @Prop() readonly showQuitButton: boolean = false;

  /**
   * Set to `true` to display the 'Open log' link.
   */
  @Prop() readonly showOpenLogLink: boolean = false;

  /**
   * Set to `true` to display "Docker is not installed" error message and to display "Download Docker" button.
   */
  @Prop() readonly showDockerMissingError: boolean = false;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  render() {
    const renderOpenLogLink = this.showOpenLogLink && this.openLogCallback;
    const renderInstallationErrorMessage =
      this.showQuitButton && this.quitCallback && this.messages.length > 0;
    const renderInitializingMessage =
      !(this.showQuitButton && this.quitCallback) && this.messages?.length > 0;
    const renderErrorMessage =
      this.showDockerMissingError || !renderInitializingMessage;

    let installationErrorMessage =
      this.#componentLocale.warnings.installationFailed;
    if (renderOpenLogLink) {
      installationErrorMessage = `${installationErrorMessage} ${
        this.#componentLocale.warnings.checkTheLog
      }`;
    }

    return (
      <Host>
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <div
          class={{
            scrollable: true,
            wrapper: true
          }}
        >
          <div
            class="side-inline-start"
            style={{ backgroundImage: `url(${COPYRIGHTS_ILLUSTRATION})` }}
            role="img"
          ></div>

          <section class="side-inline-end scrollable">
            <header class="header">
              <img
                src={LOGO_NEXT}
                alt={this.#componentLocale.illustrationAlternativeText}
              />
              <h2 class="title heading-2">{this.#componentLocale.title}</h2>
              {this.appVersion && (
                <p class="body-regular-m next-version">
                  {this.#componentLocale.nextVersion} {this.appVersion}
                </p>
              )}
              <p class="body-italic-m header__description">
                {this.#componentLocale.allRightsReserved}
              </p>
              <p class="body-italic-m header__description">
                {this.#componentLocale.copyright}
              </p>
            </header>

            <main class="main">
              {renderInitializingMessage && (
                <p
                  class={{
                    "body-regular-m": true,
                    "status--processing": true
                  }}
                >
                  {this.#componentLocale.initializingServices}
                  {ANIMATION_DOTS("tiny", "on-surface")}
                </p>
              )}

              <code
                class="log__container scrollable"
                role="status"
                aria-live="polite"
                aria-busy={this.ariaBusy}
              >
                {this.messages?.reverse().map((message, i) => {
                  return (
                    <p
                      class={{
                        "caption-regular-l": true,
                        "log__message": true,
                        "log__message--error": message.type === "error"
                      }}
                      key={this.messages.length - i}
                    >
                      {message.text}
                    </p>
                  );
                })}
              </code>

              {renderErrorMessage && (
                <p class="error-message status--error body-regular-m">
                  {renderInstallationErrorMessage && (
                    <span>{installationErrorMessage}</span>
                  )}

                  {renderInstallationErrorMessage &&
                    this.showDockerMissingError && <span> - </span>}

                  {this.showDockerMissingError && (
                    <span>{this.#componentLocale.warnings.dockerMissing}</span>
                  )}
                </p>
              )}
            </main>

            <footer class="footer">
              <div class="buttons-spacer">
                {this.showDockerMissingError && (
                  <button
                    key="download-docker"
                    class="button-secondary button-icon-and-text download-docker-button"
                    onClick={this.downloadDockerCallback}
                  >
                    {this.#componentLocale.downloadDocker}
                  </button>
                )}

                {renderOpenLogLink && (
                  <button
                    key="open-log"
                    class="button-tertiary button-icon-and-text"
                    onClick={this.openLogCallback}
                  >
                    <ch-image class="icon-m" src={OPEN_LOG_ICON}></ch-image>
                    {this.#componentLocale.openLog}
                  </button>
                )}

                {this.quitCallback && this.showQuitButton && (
                  <button class="button-secondary" onClick={this.quitCallback}>
                    {this.#componentLocale.quitGenexus}
                  </button>
                )}
              </div>
            </footer>
          </section>
        </div>
      </Host>
    );
  }
}

export type MessageType = {
  type: "info" | "error";
  text: string;
};
